import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { PERSONAS } from '../constants/personas';

const PROVINCES = [
  { code: 'AB', label: 'Alberta' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland & Labrador' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'ON', label: 'Ontario' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'QC', label: 'Quebec' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'YT', label: 'Yukon' },
];

const STEPS = ['Account', 'About you', 'Service areas', 'Assistant'];

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [areaInput, setAreaInput] = useState('');

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    brokerage: '',
    city: '',
    province: '',
    public_bio: '',
    brand_tagline: '',
    service_areas: [],
    assistant_name: 'Ava',
    avatar_filename: 'ava-avatar.png',
    voice_id: PERSONAS[0].voiceId,
  });

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const addArea = () => {
    const value = areaInput.trim();
    if (!value) return;
    if (form.service_areas.some((a) => a.toLowerCase() === value.toLowerCase())) {
      setAreaInput('');
      return;
    }
    setField('service_areas', [...form.service_areas, value]);
    setAreaInput('');
  };

  const removeArea = (value) => {
    setField('service_areas', form.service_areas.filter((a) => a !== value));
  };

  const selectPersona = (persona) => {
    setForm((f) => ({
      ...f,
      assistant_name: persona.name,
      avatar_filename: persona.file,
      voice_id: persona.voiceId,
    }));
  };

  const stepValid = () => {
    if (step === 0) {
      return form.first_name.trim() && form.last_name.trim() && form.email.trim()
        && form.password.length >= 8 && form.city.trim() && form.province;
    }
    return true;
  };

  const nextStep = () => {
    if (!stepValid()) {
      toast.error('Fill in the required fields before continuing.');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stepValid()) {
      setStep(0);
      toast.error('Fill in the required fields before continuing.');
      return;
    }
    setSubmitting(true);
    try {
      await register(form);
      toast.success(`Welcome to Agents4Agents, ${form.first_name}!`);
      navigate('/');
    } catch (err) {
      const apiError = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error;
      toast.error(apiError || 'Could not create your account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 560 }}>
        <div className="login-logo">
          <h1>Agents<span>4</span>Agents</h1>
          <p>Set up your AI assistant in a few steps</p>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {STEPS.map((label, i) => (
            <div
              key={label}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: i <= step ? 'var(--accent)' : 'var(--text-muted)',
                paddingBottom: 8,
                borderBottom: `2px solid ${i <= step ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >
              {label}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {step === 0 && (
            <>
              <div className="client-form__row" style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label" htmlFor="su-first">First name</label>
                  <input id="su-first" className="form-input" value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} required />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label" htmlFor="su-last">Last name</label>
                  <input id="su-last" className="form-input" value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="su-email">Email</label>
                <input id="su-email" type="email" className="form-input" value={form.email} onChange={(e) => setField('email', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="su-password">Password</label>
                <input id="su-password" type="password" className="form-input" placeholder="At least 8 characters" value={form.password} onChange={(e) => setField('password', e.target.value)} required />
              </div>
              <div className="client-form__row" style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label" htmlFor="su-phone">Phone (optional)</label>
                  <input id="su-phone" className="form-input" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label" htmlFor="su-brokerage">Brokerage (optional)</label>
                  <input id="su-brokerage" className="form-input" value={form.brokerage} onChange={(e) => setField('brokerage', e.target.value)} />
                </div>
              </div>
              <div className="client-form__row" style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label" htmlFor="su-city">City</label>
                  <input id="su-city" className="form-input" placeholder="e.g. St. John's" value={form.city} onChange={(e) => setField('city', e.target.value)} required />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label" htmlFor="su-province">Province</label>
                  <select id="su-province" className="form-input" value={form.province} onChange={(e) => setField('province', e.target.value)} required>
                    <option value="">Select...</option>
                    {PROVINCES.map((p) => (
                      <option key={p.code} value={p.code}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Buyers and sellers see this when they land on your chat link. You can change it anytime in Branding.
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="su-tagline">Short tagline (optional)</label>
                <input id="su-tagline" className="form-input" placeholder="e.g. Helping St. John's families find home" value={form.brand_tagline} onChange={(e) => setField('brand_tagline', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="su-bio">About you (optional)</label>
                <textarea id="su-bio" className="form-input" rows={4} placeholder="A couple sentences buyers will see about you" value={form.public_bio} onChange={(e) => setField('public_bio', e.target.value)} />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Which areas do you serve? Your assistant uses these to scope local knowledge and match listings — add as many as you like.
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="su-area">Add an area</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="su-area"
                    className="form-input"
                    placeholder="e.g. Mount Pearl"
                    value={areaInput}
                    onChange={(e) => setAreaInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addArea(); } }}
                  />
                  <button type="button" className="btn btn-secondary" onClick={addArea}>Add</button>
                </div>
              </div>
              {form.service_areas.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {form.service_areas.map((area) => (
                    <span
                      key={area}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 10px', borderRadius: 999,
                        background: 'var(--accent-dim)', border: '1px solid var(--border)',
                        fontSize: 13, color: 'var(--text-primary)',
                      }}
                    >
                      {area}
                      <button
                        type="button"
                        onClick={() => removeArea(area)}
                        aria-label={`Remove ${area}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, padding: 0 }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {form.service_areas.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  No areas added yet — you can always add these later in Branding. Your city ({form.city || 'not set'}) will be used until then.
                </p>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Pick your assistant's name, face, and voice. You can change this anytime in Branding.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10 }}>
                {PERSONAS.map((p) => {
                  const isSelected = form.avatar_filename === p.file && form.assistant_name === p.name;
                  return (
                    <button
                      key={p.file}
                      type="button"
                      onClick={() => selectPersona(p)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                        padding: '10px 6px', borderRadius: 10,
                        border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                        background: isSelected ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                        cursor: 'pointer',
                      }}
                    >
                      <img src={`/${p.file}`} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                      <span style={{ fontSize: 12.5, textTransform: 'none', color: 'var(--text-primary)' }}>{p.name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 }}>
            {step > 0 ? (
              <button type="button" className="btn btn-ghost" onClick={prevStep} disabled={submitting}>Back</button>
            ) : <span />}
            {step < STEPS.length - 1 ? (
              <button type="button" className="btn btn-primary" onClick={nextStep}>Continue</button>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating account...' : 'Create my account'}
              </button>
            )}
          </div>
        </form>

        <div className="login-demo">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
